/** Phòng khám (exam rooms). Real /api/v1/rooms. */
import { apiClient } from "./client";

export interface Room {
  id: string;
  branchId: string;
  code: string;
  name: string;
  doctorProfileId: string | null;
  doctorName: string | null;
  /** Owning khoa (Department) id — nullable ("— Không thuộc khoa —"). */
  departmentId: string | null;
  active: boolean;
}

export interface RoomInput {
  code?: string;
  name: string;
  doctorProfileId?: string | null;
  departmentId?: string | null;
  active?: boolean;
}

export const roomsApi = {
  list(activeOnly = false): Promise<Room[]> {
    return apiClient
      .get<Room[]>("/v1/rooms", { params: { activeOnly } })
      .then((r) => r.data);
  },
  create(input: RoomInput): Promise<string> {
    return apiClient.post<{ id: string }>("/v1/rooms", input).then((r) => r.data.id);
  },
  update(id: string, input: RoomInput): Promise<void> {
    return apiClient.put(`/v1/rooms/${id}`, input).then(() => undefined);
  },
  archive(id: string): Promise<void> {
    return apiClient.delete(`/v1/rooms/${id}`).then(() => undefined);
  },
  /** Hard delete (permanent). Throws room_in_use (409) if the room still has appointments. */
  remove(id: string): Promise<void> {
    return apiClient.delete(`/v1/rooms/${id}/permanent`).then(() => undefined);
  },
};

export const roomQueryKeys = {
  list: (activeOnly: boolean) => ["rooms", activeOnly] as const,
};

/**
 * How a room is named in a picker: `<mã> · <tên>` (#19033).
 *
 * <p>A formatter rather than a string literal at each call site, because the literal has now been
 * forgotten three times in the same screen — #18897, #18988 and this ticket are all one picker
 * rendering less than its neighbour two hundred lines away. The code alone is unreadable here: on
 * an MQ-synced catalogue it is `MQ:138`, an id the hospital assigned that says nothing about which
 * room it is (`MQ:140` and `MQ:141` are both in Phòng 08). Picking by it is guesswork, and picking
 * the wrong room issues the patient a number in another room's queue when `queueScope: ROOM`.
 *
 * <p>A room with no code falls back to the name alone rather than rendering a leading separator.
 */
export function roomLabel(room: Pick<Room, "code" | "name">): string {
  const code = room.code?.trim();
  const name = room.name?.trim();
  if (!code) return name ?? "";
  if (!name) return code;
  return `${code} · ${name}`;
}

/** Assign a checked-in appointment to a room ("gọi vào phòng"). Returns the room number. */
export function assignRoom(appointmentId: string, roomId: string): Promise<number> {
  return apiClient
    .post<{ roomNo: number }>(`/v1/appointments/${appointmentId}/assign-room`, { roomId })
    .then((r) => r.data.roomNo);
}
